import { useEffect, useState } from "react";
import { formatUnits } from "ethers";
import { formatContractError } from "@/lib/error";
import { useContracts } from "@/hooks/useContracts";
import { ASSETS } from "@/lib/contracts";
import { Button } from "@/components/ui/button";
import { Loader2, Wallet } from "lucide-react";
import { refreshAfterTx } from "@/lib/refreshAll";

interface ClaimPanelProps {
  tokenId?: number | null;
  onDone?: () => void;
}

export function ClaimPanel({ tokenId, onDone }: ClaimPanelProps) {
  const { rounds, vault, connected, requestConnect } = useContracts();
  const [balances, setBalances] = useState<bigint[]>([0n, 0n, 0n]);
  const [decimals, setDecimals] = useState<number[]>([18, 18, 18]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenId) return;
    setIsLoading(true);
    Promise.all([
      vault.balancesOf(tokenId),
      ...ASSETS.map((_, idx) => vault.assetToken(idx).then((addr: string) => getDecimals(addr))),
    ])
      .then(([result, ...decs]) => {
        const amounts = (result as [string[], bigint[]])[1];
        setBalances(amounts);
        setDecimals(decs.map((d) => Number(d)));
      })
      .catch(() => {
        setBalances([0n, 0n, 0n]);
      })
      .finally(() => setIsLoading(false));
  }, [tokenId, vault]);

  const totalValue = balances.some((b) => b > 0n);

  const handleClaim = async () => {
    if (!tokenId) return;
    setError(null);
    setIsPending(true);
    try {
      setStep("Sync");
      const syncTx = await rounds.sync([tokenId]);
      await syncTx.wait();

      setStep("Settle");
      const settleTx = await rounds.settle();
      await settleTx.wait();

      setStep("Deliver");
      const deliverTx = await rounds.deliver([tokenId]);
      await deliverTx.wait();

      setStep("Claim");
      const claimTx = await vault.claim(tokenId);
      await claimTx.wait();
      await refreshAfterTx(tokenId);
      onDone?.();
    } catch (err) {
      setError(formatContractError(err));
    } finally {
      setIsPending(false);
      setStep(null);
    }
  };

  if (!connected) {
    return (
      <Button variant="pixel" size="pixel" className="w-full" onClick={requestConnect}>
        Connect Wallet
      </Button>
    );
  }

  if (!tokenId) {
    return <p className="text-sm text-muted-foreground">Select a cat to claim rewards.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-background p-4">
        <p className="font-pixel text-xs text-primary">VAULT BALANCE</p>
        <div className="mt-2 space-y-1">
          {ASSETS.map((asset, idx) => (
            <div key={asset.symbol} className="flex justify-between text-sm">
              <span className="font-mono">{asset.symbol}</span>
              <span className="text-muted-foreground">
                {isLoading ? "-" : formatUnits(balances[idx] ?? 0n, decimals[idx] ?? 18)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Button
        variant="pixel"
        size="pixel"
        className="w-full"
        onClick={handleClaim}
        disabled={isPending || isLoading || !totalValue}
      >
        {isPending ? (
          <span className="flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            {step}
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Wallet size={16} />
            Claim All
          </span>
        )}
      </Button>

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}

async function getDecimals(tokenAddress: string): Promise<number> {
  try {
    const { Contract, JsonRpcProvider } = await import("ethers");
    const provider = new JsonRpcProvider(
      import.meta.env.VITE_RPC_URL as string,
      Number(import.meta.env.VITE_CHAIN_ID ?? 4663)
    );
    const token = new Contract(tokenAddress, ["function decimals() view returns (uint8)"], provider);
    const d = await token.decimals();
    return Number(d);
  } catch {
    return 18;
  }
}
